import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
// Components
import Button from "../../../../components/buttons/buttons";
import Table from "../../../../components/tables/tableCols1";
import TableSkeleton from "../../../../components/skeleton/table";
import TableFilter from "../../../../components/tables/tableFilter";
import { Beat } from "../../../../plugins/spinners.plugin";
import EmptyState from "../../../../assets/images/emptyState/user.svg";
import { Pagination } from "../../../../components/pagination/pagination";
import { AllFiltersModal } from "../../../../components/modals/allFiltersModal";
import { writeFileWithXLSX } from "../../../../components/exports/xlsx";
import FilterReferralsDropDown from "../../../../components/dropDown/filter/filterReferrals";
// Actions
import {
  filterReferralsAction,
  searchReferralsAction,
  getAllReferralsNoPagination,
} from "../../../../redux/referral/referralActions";
import referralType from "../../../../redux/referral/referralTypes";

export default function Referrals() {
  const dispatch = useDispatch();
  const { page } = useParams();
  const [data, setData] = useState([]);
  const { allReferrals, filtering, searching, downloading } = useSelector(
    (state) => state.referral
  );

  let lastMonth = new Date();
  lastMonth.setDate(lastMonth.getDate() - 30);

  const [isFilterOpen, setFilterState] = useState(false);
  const [input, setInput] = useState({
    search: "",
  });

  const [payLoad] = useState({
    start_date: lastMonth,
    end_date: new Date(),
  });

  useEffect(() => {
    dispatch(filterReferralsAction(payLoad, page));
    // eslint-disable-next-line
  }, [page]);

  function createTableData(data) {
    let dataToMap = data;
    if (Array.isArray(dataToMap)) {
      let outputArray = [];
      dataToMap.map((referral, index) => {
        let show = {
          id: index + 1,
          user_id: referral?.user_id,
          count: referral?.referral_count,
          code: referral?.referral_code,
          referrer_code: referral?.referrer_code ?? "No referee",
          created: (
            <span>{moment(referral?.created_at).format("MMM DD, YYYY")}</span>
          ),
        };
        outputArray.push({ show });
        return true;
      });
      return outputArray;
    }
  }

  function createTableDataDownload(data) {
    let dataToMap = data;
    if (Array.isArray(dataToMap)) {
      let outputArray = [];
      dataToMap.map((referral, index) => {
        let show = {
          id: index + 1,
          user_id: referral?.user_id,
          count: referral?.referral_count,
          code: referral?.referral_code,
          referrer_code: referral?.referrer_code ?? "No referee",
          created: moment(referral?.created_at).format("MMM DD, YYYY"),
        };
        outputArray.push(show);
        return true;
      });
      return outputArray;
    }
  }

  useEffect(() => {
    const dataSet = createTableData(allReferrals?.data);
    setData(dataSet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, allReferrals]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    e.preventDefault();
    setInput({ ...input, [name]: value });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      doSearch();
    }
  };

  const showSearch = (search) => {
    if (input?.search?.length > 2) {
      let dataSet = createTableData(search);
      setData(dataSet);
    }
  };

  const removeSearch = () => {
    if (input?.search?.length === 0) {
      let dataSet = createTableData(allReferrals?.data);
      setData(dataSet);
      dispatch({
        type: referralType["SEARCH_REFERRALS"],
        payLoad: { data: [] },
      });
    }
  };

  async function doSearch() {
    try {
      let response = await dispatch(searchReferralsAction(input));
      if (response?.status === "success") {
        showSearch(response?.data);
      }
      if (!response?.status) {
        showSearch([]);
      }
    } catch (error) {}
  }

  useEffect(() => {
    if (input?.search?.length === 0) {
      removeSearch();
    }
    // eslint-disable-next-line
  }, [input, dispatch]);

  const handleDownload = async () => {
    const response = await dispatch(
      getAllReferralsNoPagination({
        ...payLoad,
        pagination: 0,
      })
    );
    if (response) {
      const newData = createTableDataDownload(response?.data);
      writeFileWithXLSX(newData, {
        filename: "All_Referrals.xlsx",
        download: true,
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        hideHeader: false,
        id: "checkbox-table-column",
        // First group columns
        columns: [
          {
            Header: "S/N",
            accessor: "show.id",
          },
          {
            Header: "User ID",
            accessor: "show.user_id",
          },
          {
            Header: "Referral Count",
            accessor: "show.count",
          },
          {
            Header: "Referral Code",
            accessor: "show.code",
          },
          {
            Header: "Referee Code",
            accessor: "show.referrer_code",
          },
          {
            Header: "Created On",
            accessor: "show.created",
          },
        ],
      },
    ],
    []
  );
  return (
    <div data-test="referrals-page">
      <TableFilter
        title={"Referrals"}
        results={allReferrals?.to}
        totalResults={allReferrals?.total}
        onChange={handleChange}
        handleKeyDown={handleKeyDown}
        filter={
          <AllFiltersModal
            isFilterOpen={isFilterOpen}
            setFilterState={setFilterState}
          >
            <FilterReferralsDropDown
              isFilterOpen={isFilterOpen}
              setFilterState={setFilterState}
              payLoad={payLoad}
            />
          </AllFiltersModal>
        }
        searchPlaceholder="Name or Email"
      >
        <Button.Secondary
          title={downloading ? <Beat color={"#CE2600"} /> : "Download CSV"}
          disabled={!data}
          type="button"
          onClick={handleDownload}
        />
      </TableFilter>

      <div className="border-b border-[#E4E4F3]"></div>

      <div className="bg-white rounded-md py-4 px-3">
        {filtering || searching ? (
          <TableSkeleton />
        ) : data?.length > 0 ? (
          <Table
            columns={columns}
            data={data?.length > 0 ? data : []}
            className="w-full"
          />
        ) : (
          <div className="py-24 text-center">
            <div className="flex justify-center">
              <img src={EmptyState} className="w-auto" alt="no data" />
            </div>
            <h3 className="mt-5 text-xl font-bold">Referrals</h3>
            <p className="mt-2 text-sm text-[#9CA3AF]">
              You do not have any referrals yet.
            </p>
          </div>
        )}
      </div>

      <Pagination data={allReferrals} route={"admin/users/referral"} />
    </div>
  );
}
